import {
    buildHierarchyFlatOrder,
    getInheritedDeadline,
    getInheritedTimeline,
    getChildren,
} from './taskHierarchyUtils';
import {
    formatDuration,
    getStatusDisplayMeta,
    getVisibleTaskTags,
} from './taskSchedulingUtils';
import { formatPlainText, stripHtmlToPlainText } from './pdfTextUtils';

const PRIORITY_LABELS = {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
    '': 'None',
    NONE: 'None',
};

const formatShareDate = value => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    } catch {
        return '';
    }
};

const getOwnerName = (task, projectUsersAll) => {
    const userId = task?.assigned_to || task?.created_by;
    if (!userId) {
        const offline = (task?.assigned_to_offline || '').trim();
        return offline || '';
    }
    const match = projectUsersAll?.find(user => user?.value?.user_id === userId);
    return formatPlainText(match?.value?.full_name || '');
};

const taskHasChildren = (task, allTasks) => getChildren(task?.id, allTasks).length > 0;

const getDeadlineValue = (task, allTasks) => {
    if (taskHasChildren(task, allTasks)) {
        return task.deadline || getInheritedDeadline(task, allTasks).deadline;
    }
    return task.deadline;
};

const orderSelectedTasks = (allTasks, selectedIds, customOrder) => {
    const idSet = new Set(selectedIds.map(id => String(id)));
    const orderedIds = buildHierarchyFlatOrder(allTasks, customOrder, null);
    const taskMap = new Map(allTasks.map(task => [String(task.id), task]));
    return orderedIds.map(id => taskMap.get(String(id))).filter(task => task && idSet.has(String(task.id)));
};

const formatTaskBlock = (task, index, context) => {
    const { allTasks, projectUsersAll, selectedProject } = context;
    const lines = [];

    const taskName = formatPlainText(task.task || '');
    if (taskName) {
        lines.push(`${index}. ${taskName}`);
    }

    const owner = getOwnerName(task, projectUsersAll);
    if (owner) lines.push(`Owner: ${owner}`);

    const priority = PRIORITY_LABELS[task.priority] || '';
    if (priority && priority !== 'None') lines.push(`Priority: ${priority}`);

    const statusMeta = getStatusDisplayMeta(task.status, selectedProject);
    if (statusMeta?.label) lines.push(`Status: ${statusMeta.label}`);

    const deadlineValue = getDeadlineValue(task, allTasks);
    const deadlineText = formatShareDate(deadlineValue);
    if (deadlineText) lines.push(`Deadline: ${deadlineText}`);

    const timeline = getInheritedTimeline(task, allTasks);
    const start = formatShareDate(timeline?.start_date);
    const end = formatShareDate(timeline?.end_date);
    if (start || end) {
        const timelineText = start && end ? `${start} - ${end}` : start || end;
        lines.push(`Timeline: ${timelineText}`);
    }

    if (task.duration != null) {
        const durationText = formatDuration(task.duration);
        if (durationText) lines.push(`Duration: ${durationText}`);
    }

    if (task.workstream_repr) {
        lines.push(`Workstream: ${formatPlainText(task.workstream_repr)}`);
    }

    if (task.project_phase) {
        lines.push(`Project Phase: ${formatPlainText(task.project_phase)}`);
    }

    const tags = getVisibleTaskTags(task)
        .map(formatPlainText)
        .filter(Boolean);
    if (tags.length) {
        lines.push(`Tags: ${tags.join(', ')}`);
    }

    const description = stripHtmlToPlainText(task.description);
    if (description) lines.push(`Description: ${description}`);

    if (task.parent_task) {
        const parent = allTasks.find(t => t.id === task.parent_task);
        const parentName = formatPlainText(parent?.task || '');
        if (parentName) lines.push(`Parent: ${parentName}`);
    }

    return lines.join('\n');
};

/**
 * Format selected tasks as deterministic plain-text update (no AI).
 */
export const formatShareUpdateAsSelected = ({
    tasks = [],
    selectedIds = [],
    customOrder = null,
    selectedProject = null,
    projectUsersAll = [],
}) => {
    const allTasks = tasks || [];
    const ordered = orderSelectedTasks(allTasks, selectedIds, customOrder);
    if (!ordered.length) return '';

    const projectName = formatPlainText(selectedProject?.project_name || 'Project Update');
    const currentDate = formatShareDate(new Date());
    const header = currentDate ? `${projectName} — Task Update` : `${projectName} — Task Update`;

    const context = { allTasks, projectUsersAll, selectedProject };
    const blocks = ordered.map((task, idx) => formatTaskBlock(task, idx + 1, context));

    return `${header}\n\n${blocks.join('\n\n')}`.trim();
};

export const buildDefaultEmailSubject = projectName => {
    const name = formatPlainText(projectName || 'Project Update');
    const dateStr = formatShareDate(new Date());
    return `${name} — Task Update — ${dateStr}`;
};

export default formatShareUpdateAsSelected;
